import { useCallback, useEffect, useState } from 'react';
import { recalculateTiers, saveTier, tiers as loadTiers, type TierView } from './api';
import type { LoyaltyTranslate } from './i18n';
import {
  Button,
  Card,
  DataTable,
  Field,
  MoneyInput,
  Notice,
  TextInput,
  Reveal,
  type Column } from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { formatMoney } from '../../shell/format';

export interface TiersTabProps {
  readonly t: LoyaltyTranslate;
  readonly onError: (message: string) => void;
  readonly canManage: boolean;
}

interface TierDraft {
  readonly code: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly minLifetimeAccruedMinor: number | null;
  readonly minSpendMinor: number | null;
  readonly minVisits: number | null;
  readonly periodDays: number | null;
}

const EMPTY: TierDraft = {
  code: '',
  name: '',
  sortOrder: 10,
  minLifetimeAccruedMinor: null,
  minSpendMinor: null,
  minVisits: null,
  periodDays: null,
};

function toDraft(tier: TierView): TierDraft {
  const c = tier.entryCondition as Record<string, number | undefined>;
  return {
    code: tier.code,
    name: tier.name,
    sortOrder: tier.sortOrder,
    minLifetimeAccruedMinor: c['minLifetimeAccruedMinor'] ?? null,
    minSpendMinor: c['minSpendMinor'] ?? null,
    minVisits: c['minVisits'] ?? null,
    periodDays: c['periodDays'] ?? null,
  };
}

/** Условие входа читаемой строкой вместо JSON, который было видно на экране. */
function describeCondition(t: LoyaltyTranslate, tier: TierView): string {
  const c = tier.entryCondition as Record<string, number | undefined>;
  const parts: string[] = [];
  if (c['minLifetimeAccruedMinor'] !== undefined) {
    parts.push(t('tiers.cond.accrued', { value: formatMoney(c['minLifetimeAccruedMinor']) }));
  }
  if (c['minSpendMinor'] !== undefined) {
    parts.push(t('tiers.cond.spend', { value: formatMoney(c['minSpendMinor']) }));
  }
  if (c['minVisits'] !== undefined) {
    parts.push(t('tiers.cond.visits', { value: c['minVisits'] }));
  }
  if (parts.length === 0) return t('tiers.cond.none');
  const window =
    c['periodDays'] === undefined
      ? t('tiers.cond.lifetime')
      : t('tiers.cond.window', { days: c['periodDays'] });
  return `${parts.join(', ')} · ${window}`;
}

/**
 * Уровни программы. Раньше на вкладке была ровно одна кнопка — «Пересчитать
 * уровни» — и надпись «Уровни не заведены»: серебро, золото и платину нельзя
 * было создать из интерфейса вообще, хотя POST /api/v1/loyalty/tiers существует.
 * Вкладка была обречена остаться пустой навсегда.
 */
export function TiersTab({ t, onError, canManage }: TiersTabProps) {
  const toast = useToast();
  const [rows, setRows] = useState<readonly TierView[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<TierDraft>(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setRows(await loadTiers());
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const conditionEmpty =
    draft.minLifetimeAccruedMinor === null &&
    draft.minSpendMinor === null &&
    draft.minVisits === null;

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      await saveTier({
        code: draft.code.trim(),
        name: draft.name.trim(),
        sortOrder: draft.sortOrder,
        entryCondition: {
          ...(draft.minLifetimeAccruedMinor !== null
            ? { minLifetimeAccruedMinor: draft.minLifetimeAccruedMinor }
            : {}),
          ...(draft.minSpendMinor !== null ? { minSpendMinor: draft.minSpendMinor } : {}),
          ...(draft.minVisits !== null ? { minVisits: draft.minVisits } : {}),
          ...(draft.periodDays !== null ? { periodDays: draft.periodDays } : {}),
        },
        benefits: {},
      });
      toast.ok(editing ? t('tiers.updated') : t('tiers.created'));
      setDraft(EMPTY);
      setEditing(null);
      await reload();
    } catch (err) {
      toast.error((err as Error).message, (err as { code?: string }).code);
    } finally {
      setBusy(false);
    }
  }

  const columns: ReadonlyArray<Column<TierView>> = [
    { key: 'order', header: t('tiers.order'), render: (r) => r.sortOrder, numeric: true },
    {
      key: 'code',
      header: t('tiers.code'),
      render: (r) => <code className="font-mono text-xs">{r.code}</code>,
    },
    { key: 'name', header: t('tiers.name'), render: (r) => r.name },
    {
      key: 'cond',
      header: t('tiers.condition'),
      render: (r) => <span className="text-ink-muted">{describeCondition(t, r)}</span>,
    },
    ...(canManage
      ? [
          {
            key: 'act',
            header: '',
            render: (r: TierView) => (
              <Button
                onClick={() => {
                  setDraft(toDraft(r));
                  setEditing(r.code);
                }}
              >
                {t('tiers.edit')}
              </Button>
            ),
          },
        ]
      : []),
  ];

  // Скелет вместо строчки «Загрузка…»: держит высоту будущей таблицы, поэтому
  // страница не подпрыгивает в момент прихода данных.
  return (
    <section className="space-y-4">
      <Card
        title={t('tiers.title')}
        hint={t('tiers.hint')}
        actions={
          canManage ? (
            <Button
              disabled={busy}
              onClick={() => {
                setBusy(true);
                recalculateTiers()
                  .then((r) => toast.ok(t('tiers.recalculated', r)))
                  .catch((err: Error) => toast.error(err.message))
                  .finally(() => setBusy(false));
              }}
            >
              {t('tiers.recalculate')}
            </Button>
          ) : null
        }
      >
        <DataTable
          loading={loading}
          rows={rows}
          columns={columns}
          empty={t('tiers.empty')}
          emptyReason={canManage ? t('tiers.emptyReason') : undefined}
          rowKey={(r) => r.id}
          activeKey={rows.find((r) => r.code === editing)?.id}
        />
      </Card>

      {/*
        * Форма стоит под таблицей уровней. При нажатии «Править» в строке она
        * менялась молча за пределами экрана — теперь подтягивает себя в вид.
        * `when={editing}` не срабатывает на пустом значении: форма создания
        * всегда на месте и дёргать страницу при первой отрисовке незачем.
        */}
      {canManage ? (
        <Reveal when={editing}>
        <Card title={editing ? t('tiers.editTitle', { code: editing }) : t('tiers.newTitle')}>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label={t('tiers.code')} hint={t('tiers.codeHint')} required>
              <TextInput
                value={draft.code}
                disabled={Boolean(editing)}
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
              />
            </Field>
            <Field label={t('tiers.name')} required>
              <TextInput
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field label={t('tiers.order')} hint={t('tiers.orderHint')}>
              <TextInput
                type="number"
                min={0}
                value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
              />
            </Field>
          </div>

          <p className="mt-4 text-sm font-medium text-ink">{t('tiers.conditionTitle')}</p>
          <p className="mt-0.5 text-xs text-ink-muted">{t('tiers.conditionHint')}</p>
          <div className="mt-2 grid gap-3 md:grid-cols-4">
            <Field label={t('tiers.minAccrued')}>
              <MoneyInput
                valueMinor={draft.minLifetimeAccruedMinor}
                onChangeMinor={(minor) => setDraft({ ...draft, minLifetimeAccruedMinor: minor })}
              />
            </Field>
            <Field label={t('tiers.minSpend')}>
              <MoneyInput
                valueMinor={draft.minSpendMinor}
                onChangeMinor={(minor) => setDraft({ ...draft, minSpendMinor: minor })}
              />
            </Field>
            <Field label={t('tiers.minVisits')}>
              <TextInput
                type="number"
                min={0}
                value={draft.minVisits ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    minVisits: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </Field>
            <Field label={t('tiers.periodDays')} hint={t('tiers.periodHint')}>
              <TextInput
                type="number"
                min={1}
                value={draft.periodDays ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    periodDays: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </Field>
          </div>

          {conditionEmpty ? (
            <div className="mt-3">
              <Notice tone="warn">{t('tiers.conditionRequired')}</Notice>
            </div>
          ) : null}

          <div className="mt-3 flex gap-2">
            <Button
              tone="primary"
              disabled={busy || !draft.code.trim() || !draft.name.trim() || conditionEmpty}
              onClick={() => void submit()}
            >
              {editing ? t('tiers.save') : t('tiers.create')}
            </Button>
            {editing ? (
              <Button
                onClick={() => {
                  setDraft(EMPTY);
                  setEditing(null);
                }}
              >
                {t('common.cancel')}
              </Button>
            ) : null}
          </div>
        </Card>
        </Reveal>
      ) : null}
    </section>
  );
}
